import FailedGenerationDialog from 'components/Grading/FailedGenerationDialog';
import { useApp } from 'contexts/app';
import { useAppBar } from 'contexts/appBar';
import { useViewport } from 'contexts/viewport';
import Desktop from 'pages/Grading/desktop';
import Mobile from 'pages/Grading/mobile';
import { PageContext } from 'pages/Grading/PageProvider';
import Tablet from 'pages/Grading/tablet';
import { useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Icon, Loading, Stack } from 'shell/components';

const PageLayout = () => {
    const { isLoading } = useApp();
    const { setNode } = useAppBar();
    const { device } = useViewport();
    const { t } = useTranslation('assignment');

    const contextProps = useContext(PageContext);

    const {
        currentAssignment,
        submissions,
        currentSubmission,
        hasChanges,
        form,
        isCurrentFormValid,
        isGradesGenerating,
        isFeedbackGenerating,
        showFailedDialog,
        setShowFailedDialog,
        handleClickAcceptSuggestions,
        handleClickConfirm,
        handleClickClose,
    } = contextProps;

    const areAllCriteriaSelected = currentAssignment?.rubric?.criteria
        .map(({ id }) => id)
        .every((id) => {
            return Object.keys(form.criteria).includes(id);
        });
    const isGenerating = isGradesGenerating || isFeedbackGenerating;
    const allOtherSubmissions = submissions?.filter(({ id }) => id !== currentSubmission?.id);
    const areAllOtherSubmissionsConfirmed = allOtherSubmissions?.every(({ grading }) => Boolean(grading));
    const isCurrentSubmissionNotConfirmed = !Boolean(currentSubmission?.grading);
    const areAllSubmissionsButCurrentConfirmed = areAllOtherSubmissionsConfirmed && isCurrentSubmissionNotConfirmed;

    const AcceptSuggestionsButton = useMemo(
        () =>
            currentAssignment?.ai_grading_enabled ? (
                <Button
                    // TODO this needs to be here due to accessibility reasons
                    // we need to find a better way to do this
                    sx={{ fontWeight: 'bold' }}
                    color={'success'}
                    disabled={areAllCriteriaSelected}
                    startIcon={<Icon kind={'checkmark'} />}
                    tooltip={{
                        component: t(
                            'grading-section.accept-suggestions-tooltip',
                            'Apply suggested grades to all ungraded criteria'
                        ),
                    }}
                    variant={'secondary'}
                    onClick={handleClickAcceptSuggestions}
                >
                    {device === 'mobile'
                        ? t('grading-section.actions.accept', 'Accept')
                        : t('grading-section.actions.accept-suggestions', 'Accept Suggestions')}
                </Button>
            ) : null,
        [areAllCriteriaSelected, currentAssignment?.ai_grading_enabled, device, handleClickAcceptSuggestions, t]
    );

    const ExitButton = useMemo(
        () => (
            <Button color={'neutral'} disabled={isGenerating} variant={'tertiary'} onClick={handleClickClose}>
                {t('common:controls.exit', 'Exit')}
            </Button>
        ),
        [handleClickClose, isGenerating, t]
    );

    const ConfirmButton = useMemo(
        () => (
            <Button
                className={'joyride-step-11'}
                disabled={isGenerating || !isCurrentFormValid || !hasChanges}
                tooltip={{
                    component: t('grading-section.actions.confirm-grades-and-feedback', 'Confirm grades & feedback'),
                    maxWidth: '200px',
                }}
                onClick={() => handleClickConfirm(!areAllSubmissionsButCurrentConfirmed)}
            >
                {device === 'mobile'
                    ? t('common:controls.confirm', 'Confirm')
                    : areAllSubmissionsButCurrentConfirmed
                      ? t('grading-section.actions.confirm-and-finish', 'Confirm & Finish')
                      : t('grading-section.actions.confirm-and-continue', 'Confirm & Continue')}
            </Button>
        ),
        [
            areAllSubmissionsButCurrentConfirmed,
            device,
            handleClickConfirm,
            hasChanges,
            isCurrentFormValid,
            isGenerating,
            t,
        ]
    );

    useEffect(() => {
        if (isLoading || !currentAssignment || !currentSubmission) return setNode(null);

        setNode(
            <Stack direction={'row'} justifyContent={'space-between'}>
                {ExitButton}
                <Stack direction={'row'} spacing={4}>
                    {AcceptSuggestionsButton}
                    {ConfirmButton}
                </Stack>
            </Stack>,
            { background: 'primary' }
        );

        return () => setNode(null);
    }, [AcceptSuggestionsButton, ConfirmButton, ExitButton, currentAssignment, currentSubmission, isLoading, setNode]);

    const renderResponsiveLayout = (device) => {
        return {
            mobile: <Mobile {...contextProps} />,
            tablet: <Tablet {...contextProps} />,
            desktop: <Desktop {...contextProps} />,
        }[device];
    };

    return isLoading || !currentAssignment || !currentSubmission ? (
        <Loading />
    ) : (
        <>
            {renderResponsiveLayout(device)}
            <FailedGenerationDialog open={showFailedDialog} onClose={() => setShowFailedDialog(false)} />
        </>
    );
};

export default PageLayout;
